import type { ReactNode } from "react";
import { Empty, PageTitle } from "../ui";
import { pageAt, permitted } from "./nav";
import { ScopeNavLink } from "./scope";
import { useSessionIfAny } from "./session";

/** A page that takes a capability the session's role holds nowhere, opened by
 * its address (UX-009, 2026-11-01 review). The rail never draws such a page
 * (`workspacesFor`), so the person came by a link or a typed address, and a
 * red refusal box there reads as a fault. This draws one neutral line in its
 * place, in COPY.md's *Your role cannot …* form, and names no capability.
 *
 * **Presentation only, and the server is still the authority** (§6.2): a role
 * the session holds anywhere draws the page, which then asks the server and
 * shows its answer at the scope picked. A superuser-only page is not wrapped:
 * its reads answer 404 below the flag (§6.3), and it keeps that answer. While
 * the session has not loaded, the page is drawn as before. */
export function ForRole({ to, children }: { to: string; children: ReactNode }) {
  const session = useSessionIfAny();
  const page = pageAt(to);
  if (!session?.me || permitted(session.me, page)) return <>{children}</>;
  return (
    <>
      <div className="page-head"><PageTitle>{page.label}</PageTitle></div>
      <Empty title={`Your role cannot open ${page.label}.`}
        info="An owner or an administrator of your organization or customer can give you a role that does."
        action={<ScopeNavLink to="/">Back to the overview</ScopeNavLink>} />
    </>
  );
}
